import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { COMBINED_FEED_QUERY } from "../../../GraphQL/combinedFeed";
import { useUser } from "../../../Hooks/useUser";

export interface ICombinedFeedConfig {
  source:
    | "followingClips"
    | "playedWithClips"
    | "proPlayerClips"
    | "shapedRecommendedClips";
  reason: string;
  maxItems: number;
  priority: number;
}

export interface ICombinedClip {
  id: string;
  shareId: string;
  title: string;
  thumbnailUrl: string;
  url?: string;
  views: number;
  createdAt: string;
  type?: string;
  user: {
    _id: string;
    username: string;
    avatarUrl?: string;
  };
  reason: string;
  priority: number;
}

export type TExtendedCombinedClip = ICombinedClip &
  Pick<ICombinedFeedConfig, "reason" | "priority">;

const DEFAULT_FEED_CONFIG: ICombinedFeedConfig[] = [
  {
    source: "followingClips",
    reason: "Following",
    maxItems: 4,
    priority: 1,
  },
  {
    source: "playedWithClips",
    reason: "Played With",
    maxItems: 3,
    priority: 2,
  },
  {
    source: "proPlayerClips",
    reason: "PRO Player",
    maxItems: 5,
    priority: 3,
  },
  {
    source: "shapedRecommendedClips",
    reason: "Suggested for you",
    maxItems: 3,
    priority: 4,
  },
];

const interleaveClipsBySources = (clips: any[]) => {
  const clipsBySource = clips.reduce(
    (acc, clip) => {
      if (!acc[clip.reason]) acc[clip.reason] = [];
      acc[clip.reason].push(clip);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  const sources = Object.keys(clipsBySource);
  const result = [];
  let maxLength = Math.max(
    ...sources.map((source) => clipsBySource[source].length),
  );

  for (let i = 0; i < maxLength; i++) {
    for (const source of sources) {
      if (clipsBySource[source][i]) {
        result.push(clipsBySource[source][i]);
      }
    }
  }

  return result;
};

const interleaveClipsByPriority = (clips: any[]) => {
  const clipsBySource = clips.reduce(
    (acc, clip) => {
      if (!acc[clip.reason]) acc[clip.reason] = [];
      acc[clip.reason].push(clip);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  const sourcesOrderedByPriority = Object.keys(clipsBySource).sort((a, b) => {
    const aClip = clipsBySource[a][0];
    const bClip = clipsBySource[b][0];
    return aClip.priority - bClip.priority;
  });

  const result = [];
  let maxLength = Math.max(
    ...sourcesOrderedByPriority.map((source) => clipsBySource[source].length),
  );

  for (let i = 0; i < maxLength; i++) {
    for (const source of sourcesOrderedByPriority) {
      if (clipsBySource[source][i]) {
        result.push(clipsBySource[source][i]);
      }
    }
  }

  return result;
};

const CombinedFeedViewModel = (
  feedConfig: ICombinedFeedConfig[] = DEFAULT_FEED_CONFIG,
  shuffleAlgorithm:
    | "random"
    | "priority"
    | "balanced"
    | "interleaved"
    | "interleavedByPriority"
    | "viewsWeighted" = "balanced",
  totalClipLimit: number = 15,
) => {
  const { allstarUser } = useUser();

  const { data, loading, error } = useQuery(COMBINED_FEED_QUERY, {
    skip: !allstarUser.loggedIn,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const combinedClips = useMemo(() => {
    if (!data?.me && !data?.shapedRecommendedClips) return [];

    const clips: TExtendedCombinedClip[] = [];
    const usedClipIds = new Set<string>();

    feedConfig.forEach((config) => {
      const sourceClips =
        config.source === "shapedRecommendedClips"
          ? data.shapedRecommendedClips || []
          : data.me[config.source] || [];
      const limitedClips = sourceClips
        .slice(0, config.maxItems)
        .map((clip: ICombinedClip) => ({
          ...clip,
          reason: config.reason,
          priority: config.priority,
        }));

      limitedClips.forEach((clip: TExtendedCombinedClip) => {
        clips.push(clip);
        usedClipIds.add(clip.id);
      });
    });

    if (clips.length < totalClipLimit) {
      const remainingSlots = totalClipLimit - clips.length;

      const proClips = (data.me?.proPlayerClips || []).filter(
        (clip: ICombinedClip) => !usedClipIds.has(clip.id),
      );
      const shapedClips = (data.shapedRecommendedClips || []).filter(
        (clip: ICombinedClip) => !usedClipIds.has(clip.id),
      );

      const proConfig = feedConfig.find((c) => c.source === "proPlayerClips");
      const shapedConfig = feedConfig.find(
        (c) => c.source === "shapedRecommendedClips",
      );

      let proIndex = 0;
      let shapedIndex = 0;
      let addedCount = 0;
      let useProNext = true;

      while (addedCount < remainingSlots) {
        let clipAdded = false;

        if (useProNext && proIndex < proClips.length) {
          const clip = proClips[proIndex];
          if (!usedClipIds.has(clip.id)) {
            clips.push({
              ...clip,
              reason: proConfig?.reason || "PRO Player",
              priority: proConfig?.priority || 3,
            });
            usedClipIds.add(clip.id);
            addedCount++;
            clipAdded = true;
          }
          proIndex++;
        } else if (!useProNext && shapedIndex < shapedClips.length) {
          const clip = shapedClips[shapedIndex];
          if (!usedClipIds.has(clip.id)) {
            clips.push({
              ...clip,
              reason: shapedConfig?.reason || "Suggested for you",
              priority: shapedConfig?.priority || 4,
            });
            usedClipIds.add(clip.id);
            addedCount++;
            clipAdded = true;
          }
          shapedIndex++;
        }

        useProNext = !useProNext;

        if (proIndex >= proClips.length && shapedIndex >= shapedClips.length) {
          break;
        }

        if (!clipAdded) {
          if (proIndex < proClips.length) {
            const clip = proClips[proIndex];
            if (!usedClipIds.has(clip.id)) {
              clips.push({
                ...clip,
                reason: proConfig?.reason || "PRO Player",
                priority: proConfig?.priority || 3,
              });
              usedClipIds.add(clip.id);
              addedCount++;
            }
            proIndex++;
          } else if (shapedIndex < shapedClips.length) {
            const clip = shapedClips[shapedIndex];
            if (!usedClipIds.has(clip.id)) {
              clips.push({
                ...clip,
                reason: shapedConfig?.reason || "Suggested for you",
                priority: shapedConfig?.priority || 4,
              });
              usedClipIds.add(clip.id);
              addedCount++;
            }
            shapedIndex++;
          }
        }
      }
    }

    let shuffledClips;
    switch (shuffleAlgorithm) {
      case "random":
        shuffledClips = clips.sort(() => Math.random() - 0.5);
        break;
      case "priority":
        shuffledClips = clips.sort((a, b) => a.priority - b.priority);
        break;
      case "interleaved":
        shuffledClips = interleaveClipsBySources(clips);
        break;
      case "interleavedByPriority":
        shuffledClips = interleaveClipsByPriority(clips);
        break;
      case "viewsWeighted":
        shuffledClips = clips.sort((a, b) => {
          const viewsDiff = (b.views || 0) - (a.views || 0);
          const randomFactor = (Math.random() - 0.5) * 1000;
          return viewsDiff + randomFactor;
        });
        break;
      case "balanced":
      default:
        shuffledClips = clips.sort((a, b) => {
          const randomFactor = Math.random() - 0.5;
          const priorityDiff = a.priority - b.priority;
          return priorityDiff * 0.3 + randomFactor;
        });
        break;
    }

    const limitedClips = shuffledClips.slice(0, totalClipLimit);

    return limitedClips;
  }, [data, feedConfig, shuffleAlgorithm, totalClipLimit]);

  const hasClips = combinedClips.length > 0;

  return {
    combinedClips,
    loading,
    error,
    hasClips,
    allstarUser,
  };
};

export { CombinedFeedViewModel };
